import React, { useState } from 'react'
import { 
  ArrowLeft,
  ChevronRight,
  Calendar,
  Save,
  RotateCcw
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import autoIncrementService from '../../services/autoIncrementService'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
// Consolidated CSS is imported globally

const AutoIncrementMasterForm = () => {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    sequenceName: '',
    description: '',
    currentValue: 1,
    prefix: '',
    digits: 4,
    incrementBy: 1,
    status: 'Active',
    createdDate: '24-09-2025'
  })

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSave = async () => {
    // Validate required fields
    if (!formData.sequenceName.trim()) {
      toast.error('Sequence Name is required')
      return
    }
    if (!formData.description.trim()) {
      toast.error('Description is required')
      return
    }
    if (formData.currentValue < 1) {
      toast.error('Current Value must be at least 1')
      return
    }
    if (formData.incrementBy < 1) {
      toast.error('Increment By must be at least 1')
      return
    }
    if (formData.digits < 1 || formData.digits > 10) {
      toast.error('Digits must be between 1 and 10')
      return
    }

    try {
      toast.info('Creating sequence...')
      const response = await autoIncrementService.createSequence(formData)
      if (response.success) {
        toast.success('Auto increment sequence created successfully!', { autoClose: 2000 })
        navigate('/settings/auto-increment-master')
      } else {
        toast.error(response.message || 'Failed to create sequence')
      }
    } catch (error) {
      console.error('Error creating sequence:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else if (error.response?.status === 409) {
        toast.error('Sequence name already exists in this company')
      } else {
        toast.error('Failed to create sequence. Please try again.')
      }
    }
  }

  const handleReset = () => {
    setFormData({
      sequenceName: '',
      description: '',
      currentValue: 1,
      prefix: '',
      digits: 4,
      incrementBy: 1,
      status: 'Active',
      createdDate: '24-09-2025'
    })
    toast.info('Form reset to default values')
  }

  const handleBack = () => {
    navigate('/settings/auto-increment-master')
  }

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Settings',
      path: '/settings',
      onClick: () => navigate('/settings')
    },
    {
      label: 'Transactions',
      path: '/settings?tab=transactions',
      onClick: () => navigate('/settings?tab=transactions')
    },
    {
      label: 'Auto Increment Master',
      path: '/settings/auto-increment-master',
      onClick: () => navigate('/settings/auto-increment-master')
    },
    {
      label: 'Create',
      path: '/settings/auto-increment-master/form',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Auto Increment Master</h1>
          <p>Manage auto-increment sequences and counters</p>
        </div>
      </div>

      {/* Form Section */}
      <div className="form-container">
        <div className="sac-form-header">
          <h2>Auto Increment Master</h2>
        </div>
          
        <form className="form-content">
          <div className="section-header">
            <h3>Sequence Configuration</h3>
          </div>
          {/* Row 1: Sequence Name, Description, Current Value, Increment By */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="sequenceName" className="form-label required">
                Sequence Name
              </label>
              <input
                type="text"
                id="sequenceName"
                name="sequenceName"
                value={formData.sequenceName}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter sequence name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="description" className="form-label required">
                Description
              </label>
              <input
                type="text"
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter sequence description"
              />
            </div>

            <div className="form-group">
              <label htmlFor="currentValue" className="form-label required">
                Current Value
              </label>
              <input
                type="number"
                id="currentValue"
                name="currentValue"
                value={formData.currentValue}
                onChange={handleInputChange}
                className="form-input"
                min="0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="incrementBy" className="form-label required">
                Increment By
              </label>
              <input
                type="number"
                id="incrementBy"
                name="incrementBy"
                value={formData.incrementBy}
                onChange={handleInputChange}
                className="form-input"
                min="1"
              />
            </div>
          </div>

          <div className="section-header">
            <h3>Format & Status</h3>
          </div>
          {/* Row 2: Prefix, Suffix, Status, Created Date */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="prefix" className="form-label">
                Prefix
              </label>
              <input
                type="text"
                id="prefix"
                name="prefix"
                value={formData.prefix}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter prefix (optional)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="digits" className="form-label">
                Digits
              </label>
              <input
                type="number"
                id="digits"
                name="digits"
                value={formData.digits}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter number of digits"
                min="1"
                max="10"
              />
            </div>

            <div className="form-group">
              <label htmlFor="status" className="form-label required">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="form-input"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="createdDate" className="form-label required">
                Created Date
              </label>
              <div className="date-input-wrapper">
                <input
                  type="text"
                  id="createdDate"
                  name="createdDate"
                  value={formData.createdDate}
                  onChange={handleInputChange}
                  className="form-input date-input"
                  placeholder="DD-MM-YYYY"
                />
                <Calendar size={16} className="calendar-icon" />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="form-actions">
            <button
              type="button"
              onClick={handleReset}
              className="btn-secondary"
            >
              <RotateCcw size={16} />
              Reset
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="btn-primary"
            >
              <Save size={16} />
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AutoIncrementMasterForm
